import { Button, Modal } from '@birdi/components-common';
import { next, blocks } from '@birdi/theme';
import { css } from '@emotion/react';
import { useState } from 'react';
import { stroot } from '@birdi/js-sdk';
import { maps as mapsAPI } from '@birdi/js-sdk/src/public';
import toast from 'react-hot-toast';

export interface DuplicateMapModalProps {
  open: boolean;
  onChange: (value: boolean) => void;
  mapId: string;
  mapName: string;
  onSuccess?: () => void;
}

export const DuplicateMapModal = ({
  open,
  onChange,
  mapId,
  mapName,
  onSuccess,
}: DuplicateMapModalProps) => {
  const [newName, setNewName] = useState(`${mapName} - Copy`);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newName.trim()) {
      toast.error('Please enter a name for the duplicated map');
      return;
    }

    setIsSubmitting(true);
    const duplicateToast = toast.loading('Duplicating map...');

    try {
      const response = await mapsAPI.duplicateMap(stroot('dupmap1'), mapId, {
        name: newName.trim(),
        duplicateLayers: true,
      });

      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to duplicate map');
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      toast.success(`Map duplicated successfully: ${response.body.name}`);
      onChange(false);

      if (onSuccess) {
        onSuccess();
      } else {
        // Default behavior: reload the page
        window.location.reload();
      }
    } catch (error) {
      console.error('Error duplicating map:', error);
      if (error instanceof Error) {
        toast.error(`Failed to duplicate map: ${error.message}`);
      } else {
        toast.error('Failed to duplicate map');
      }
    } finally {
      toast.dismiss(duplicateToast);
      setIsSubmitting(false);
    }
  };

  // Reset name when modal opens with new map
  const handleOpenChange = (value: boolean) => {
    if (value) {
      setNewName(`${mapName} - Copy`);
    }
    onChange(value);
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      <Modal.Header>Duplicate map</Modal.Header>

      <Modal.Body>
        <form
          id="duplicate-map-form"
          css={next.form.root}
          onSubmit={handleSubmit}
        >
          <label css={next.form.field.root}>
            <span>New map name</span>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter a name for the duplicated map"
              css={(theme) => css`
                ${blocks.input(theme)};
                width: 100%;
                margin-top: 0.5rem;
              `}
              autoFocus
              disabled={isSubmitting}
            />
          </label>
        </form>
      </Modal.Body>

      <Modal.Footer buttons>
        <Button variant="outline" onClick={() => onChange(false)} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="solid"
          type="submit"
          form="duplicate-map-form"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Duplicating...' : 'Duplicate'}
        </Button>
      </Modal.Footer>
    </Modal.Root>
  );
};
